// Both arches seen from above, patient's right on the viewer's left: every tooth placed along an elliptical curve at
// its real mesio-distal width. Pure numbers, computed once at module load (the trial's arch math, ported as is).

type ToothKind = "molar" | "premolar" | "canine" | "incisor";

export type ArchTooth = {
  /** Universal number, 1-32. */
  tooth: number;
  /** Center, in the arch drawing's units (no label columns). */
  x: number;
  y: number;
  /** Degrees: the curve's direction at the tooth. */
  angle: number;
  /** Drawing units along the curve and across it. */
  width: number;
  depth: number;
  kind: ToothKind;
  upper: boolean;
  /** Paths centered on 0,0: x runs along the arch. */
  outline: string;
  grooves: string;
};

const MOLARS = new Set([1, 2, 3, 14, 15, 16, 17, 18, 19, 30, 31, 32]);
const PREMOLARS = new Set([4, 5, 12, 13, 20, 21, 28, 29]);
const CANINES = new Set([6, 11, 22, 27]);

// Mesio-distal widths in mm, in drawing order (viewer's left to right).
const UPPER: readonly (readonly [number, number])[] = [
  [1, 8.5],
  [2, 9.0],
  [3, 10.0],
  [4, 7.0],
  [5, 6.6],
  [6, 7.6],
  [7, 6.5],
  [8, 8.6],
  [9, 8.6],
  [10, 6.5],
  [11, 7.6],
  [12, 6.6],
  [13, 7.0],
  [14, 10.0],
  [15, 9.0],
  [16, 8.5],
];
const LOWER: readonly (readonly [number, number])[] = [
  [32, 10.0],
  [31, 10.5],
  [30, 11.0],
  [29, 7.0],
  [28, 7.0],
  [27, 7.0],
  [26, 5.6],
  [25, 5.2],
  [24, 5.2],
  [23, 5.6],
  [22, 7.0],
  [21, 7.0],
  [20, 7.0],
  [19, 11.0],
  [18, 10.5],
  [17, 10.0],
];

const S = 5; // drawing units per mm
const A = 48; // the curve's semi-axes in mm (it runs through the tooth centers)
const B = 36;
const GAP = 0.4; // mm between neighbors
const TOP = B * S + 66; // y of the upper arch's center line
const JAW_GAP = 92; // between the two arches' center lines

export const ARCH_WIDTH = 2 * (A * S + 54);
export const ARCH_HEIGHT = TOP + JAW_GAP + B * S + 66;

/** The gum band behind the teeth: the outer and inner stroke widths. */
export const GUM_OUTER_WIDTH = (11 + 8) * S;
export const GUM_INNER_WIDTH = (11 + 2) * S;

export function formatUnits(value: number, digits = 1): string {
  return value.toFixed(digits);
}

function toothDepth(tooth: number, upper: boolean): number {
  if (MOLARS.has(tooth)) return upper ? 11.0 : 10.5;
  if (PREMOLARS.has(tooth)) return upper ? 9.0 : 8.0;
  if (CANINES.has(tooth)) return upper ? 8.0 : 7.4;
  return upper ? 7.0 : 6.2;
}

function toothKind(tooth: number): ToothKind {
  if (MOLARS.has(tooth)) return "molar";
  if (PREMOLARS.has(tooth)) return "premolar";
  if (CANINES.has(tooth)) return "canine";
  return "incisor";
}

function roundedRect(x: number, y: number, w: number, h: number, r: number): string {
  const f = (v: number) => formatUnits(v, 2);
  return (
    `M${f(x + r)},${f(y)} H${f(x + w - r)} A${f(r)},${f(r)} 0 0 1 ${f(x + w)},${f(y + r)} V${f(y + h - r)} ` +
    `A${f(r)},${f(r)} 0 0 1 ${f(x + w - r)},${f(y + h)} H${f(x + r)} A${f(r)},${f(r)} 0 0 1 ${f(x)},${f(y + h - r)} ` +
    `V${f(y + r)} A${f(r)},${f(r)} 0 0 1 ${f(x + r)},${f(y)} Z`
  );
}

/** Outline and grooves centered on 0,0; +y times outSign points to the cheek. */
function toothShape(kind: ToothKind, w: number, d: number, outSign: number): [string, string] {
  const f = (v: number) => formatUnits(v);
  if (kind === "molar") {
    return [
      roundedRect(-w / 2, -d / 2, w, d, 0.34 * Math.min(w, d)),
      `M${f(-w * 0.3)},${f(d * 0.02)} C${f(-w * 0.1)},${f(-d * 0.07)} ${f(w * 0.1)},${f(d * 0.08)} ${f(w * 0.3)},${f(-d * 0.02)} ` +
        `M${f(-w * 0.05)},${f(-d * 0.3)} C${f(-w * 0.02)},${f(-d * 0.1)} ${f(w * 0.04)},${f(d * 0.1)} ${f(w * 0.02)},${f(d * 0.3)}`,
    ];
  }
  if (kind === "premolar") {
    return [
      roundedRect(-w / 2, -d / 2, w, d, 0.47 * Math.min(w, d)),
      `M${f(-w * 0.26)},0 C${f(-w * 0.08)},${f(-d * 0.06)} ${f(w * 0.08)},${f(d * 0.06)} ${f(w * 0.26)},0`,
    ];
  }
  if (kind === "canine") {
    const k = outSign;
    return [
      `M${f(-w / 2)},${f(d * 0.05 * k)} C${f(-w / 2)},${f(-d * 0.38 * k)} ${f(-w * 0.18)},${f((-d / 2) * k)} 0,${f((-d / 2) * k)} ` +
        `C${f(w * 0.18)},${f((-d / 2) * k)} ${f(w / 2)},${f(-d * 0.38 * k)} ${f(w / 2)},${f(d * 0.05 * k)} ` +
        `C${f(w / 2)},${f(d * 0.32 * k)} ${f(w * 0.22)},${f((d / 2) * k)} 0,${f((d / 2) * k)} ` +
        `C${f(-w * 0.22)},${f((d / 2) * k)} ${f(-w / 2)},${f(d * 0.32 * k)} ${f(-w / 2)},${f(d * 0.05 * k)} Z`,
      `M0,${f(-d * 0.28 * k)} L0,${f(d * 0.18 * k)}`,
    ];
  }
  const dd = d * 0.78;
  return [
    roundedRect(-w / 2, -dd / 2, w, dd, 0.46 * dd),
    `M${f(-w * 0.3)},${f(dd * 0.2 * outSign)} L${f(w * 0.3)},${f(dd * 0.2 * outSign)}`,
  ];
}

type ArcPoint = { along: number; x: number; y: number };

/** Points along the half ellipse, viewer's left to right, with the arc length walked so far. */
function arcTable(upper: boolean, cx: number, cy: number, steps = 1600): ArcPoint[] {
  const points: ArcPoint[] = [];
  let along = 0;
  let prev: ArcPoint | undefined;
  for (let i = 0; i <= steps; i++) {
    const theta = Math.PI - (Math.PI * i) / steps;
    const x = cx + A * S * Math.cos(theta);
    const y = upper ? cy - B * S * Math.sin(theta) : cy + B * S * Math.sin(theta);
    if (prev) along += Math.hypot(x - prev.x, y - prev.y);
    prev = { along, x, y };
    points.push(prev);
  }
  return points;
}

/** Centers each tooth at its share of the curve, the whole row centered on the arch. */
function placeTeeth(
  teeth: readonly (readonly [number, number])[],
  upper: boolean,
  table: ArcPoint[],
): ArchTooth[] {
  const total = table.at(-1)?.along ?? 0;
  const need = teeth.reduce((sum, [, mm]) => sum + mm, 0) * S + GAP * S * (teeth.length - 1);
  let start = (total - need) / 2;
  let j = 0;
  return teeth.map(([tooth, mm]) => {
    const mid = start + (mm * S) / 2;
    while (j < table.length - 2 && (table[j + 1]?.along ?? Infinity) < mid) j++;
    const p0 = table[j];
    const p1 = table[j + 1];
    if (!p0 || !p1) throw new Error("arch table too short");
    const k = (mid - p0.along) / (p1.along - p0.along || 1);
    const kind = toothKind(tooth);
    const width = mm * S;
    const depth = toothDepth(tooth, upper) * S;
    const [outline, grooves] = toothShape(kind, width, depth, upper ? -1 : 1);
    start += mm * S + GAP * S;
    return {
      tooth,
      x: p0.x + (p1.x - p0.x) * k,
      y: p0.y + (p1.y - p0.y) * k,
      angle: (Math.atan2(p1.y - p0.y, p1.x - p0.x) * 180) / Math.PI,
      width,
      depth,
      kind,
      upper,
      outline,
      grooves,
    };
  });
}

/** The gum band's center line: about 160 points of the curve, trimmed short of its ends. */
function gumBand(table: ArcPoint[], trim = 22): { d: string; start: string } {
  const total = table.at(-1)?.along ?? 0;
  const step = Math.max(1, Math.floor(table.length / 160));
  const points = table.filter(
    (p, i) => i % step === 0 && trim <= p.along && p.along <= total - trim,
  );
  const xy = points.map((p) => `${formatUnits(p.x)},${formatUnits(p.y)}`);
  return {
    d:
      `M${xy[0]} ` +
      xy
        .slice(1)
        .map((p) => `L${p}`)
        .join(" "),
    start: xy[0] ?? "0,0",
  };
}

const upperTable = arcTable(true, ARCH_WIDTH / 2, TOP);
const lowerTable = arcTable(false, ARCH_WIDTH / 2, TOP + JAW_GAP);
const upperBand = gumBand(upperTable);

/** Upper teeth in drawing order (1 to 16), then lower (32 to 17). */
export const ARCH_TEETH: readonly ArchTooth[] = [
  ...placeTeeth(UPPER, true, upperTable),
  ...placeTeeth(LOWER, false, lowerTable),
];

/** The upper and the lower gum band's center lines. */
export const GUM_BANDS: readonly [string, string] = [upperBand.d, gumBand(lowerTable).d];

/** The upper band closed across the back with a gentle curve: a denture's palate. */
export const UPPER_PALATE = `${upperBand.d} Q${ARCH_WIDTH / 2},${TOP - 56} ${upperBand.start} Z`;

/** The y halfway between the arches, where the side labels sit. */
export const ARCH_MIDLINE = (TOP + TOP + JAW_GAP) / 2;

/** The x of the curve's two ends: under the patient's right side and left side. */
export const ARCH_SIDES: readonly [number, number] = [
  ARCH_WIDTH / 2 - A * S,
  ARCH_WIDTH / 2 + A * S,
];
